import { useQueryClient } from 'react-query';

import { AppMessage } from '@/common/components';
import { useAppSocket, useIsSuccessful } from '@/common/hooks';
import { AppPaginationOptions, BaseQueryParams } from '@/common/interfaces';

import { SearchConsoleAccountProcessStatus, SiteDomainsQueryKeys, SiteSearchConsoleGatewayKeys, SiteSearchConsoleQueryKeys, useGetAllSiteDomainsQuery, useGetByIdSiteDomainsQuery, useGetSitesSearchConsoleInfoQuery } from '@/modules/Domains';
import { SearchConsoleAccount } from '@/modules/DomainsApi';
import { SitesQueryKeys, useGetDossierByIdSitesQuery } from '@/modules/Sites';

interface Data {
  siteId: string;
  searchConsoleAccount: Pick<SearchConsoleAccount, 'id' | 'email' | 'isReadyToUse'>;
  searchConsoleAccountProcessStatus: SearchConsoleAccountProcessStatus;
}

interface Args {
  siteId: string;
  pageParam?: AppPaginationOptions | null;
  domainId?: string | null;
}

export const useUpdateSCAccountProcessStatus = ({
  siteId,
  domainId,
  pageParam
}: Args) => {
  const appMessageApi = AppMessage.useControls();
  const queryClient = useQueryClient();

  const isSiteDossierRequestSuccess = useIsSuccessful({ queryKey: [SitesQueryKeys.Dossier, siteId] });
  const isAllSiteDomainsRequestSuccess = useIsSuccessful({ queryKey: [SiteDomainsQueryKeys.GetAllDomains, siteId, pageParam] });
  const isSiteDomainByIdRequestSuccess = useIsSuccessful({ queryKey: [SiteDomainsQueryKeys.GetByIdDomains, siteId, domainId] });
  const isSitesSearchConsoleInfoRequestSuccess = useIsSuccessful({ queryKey: [SiteSearchConsoleQueryKeys.GetInfo, siteId] });


  useAppSocket<Data>(SiteSearchConsoleGatewayKeys.UpdateProcessingStatus, ({ lastMessage }) => {
    const { siteId: id, searchConsoleAccountProcessStatus, searchConsoleAccount } = lastMessage;

    if (siteId) {
      if (isSiteDossierRequestSuccess && siteId === id) {
        queryClient.setQueryData<
          BaseQueryParams<typeof useGetDossierByIdSitesQuery>['data']
        >([SitesQueryKeys.Dossier, id], (data) => {
          return {
            ...data,
            searchConsoleAccount,
            searchConsoleAccountProcessStatus

          } as BaseQueryParams<typeof useGetDossierByIdSitesQuery>['data'];
        });
      }

      if (isSitesSearchConsoleInfoRequestSuccess && siteId === id) {
        queryClient.setQueryData<
          BaseQueryParams<typeof useGetSitesSearchConsoleInfoQuery>['data']
        >([SiteSearchConsoleQueryKeys.GetInfo, id], (data) => {
          return {
            ...data,
            haveSearchConsoleAccount:
              searchConsoleAccount && searchConsoleAccountProcessStatus === SearchConsoleAccountProcessStatus.Setted,
          } as BaseQueryParams<typeof useGetSitesSearchConsoleInfoQuery>['data'];
        });
      }

      if (isAllSiteDomainsRequestSuccess && siteId === id) {
        queryClient.setQueryData<
          BaseQueryParams<typeof useGetAllSiteDomainsQuery>['data']
        >([SiteDomainsQueryKeys.GetAllDomains, id, pageParam], (data) => {
          return {
            ...data,
            items: data?.items?.map((item) => ({
              ...item,
              siteHaveSearchConsoleAccount:
                searchConsoleAccount && searchConsoleAccountProcessStatus === SearchConsoleAccountProcessStatus.Setted
            })) ?? data?.items
          } as BaseQueryParams<typeof useGetAllSiteDomainsQuery>['data'];
        });
      }

      if (domainId && isSiteDomainByIdRequestSuccess && siteId === id) {
        queryClient.setQueryData<
          BaseQueryParams<typeof useGetByIdSiteDomainsQuery>['data']
        >([SiteDomainsQueryKeys.GetByIdDomains, id, domainId], (data) => {
          return {
            ...data,
            site: data?.site && data.site.id === id
              ? {
                ...data.site,
                searchConsoleAccount:
                searchConsoleAccount?.id && searchConsoleAccountProcessStatus === SearchConsoleAccountProcessStatus.Setted
                  ? { id: searchConsoleAccount.id }
                  : null
              }
              : data?.site,
          } as BaseQueryParams<typeof useGetByIdSiteDomainsQuery>['data'];
        });
      }

      if (searchConsoleAccountProcessStatus === SearchConsoleAccountProcessStatus.Failed && siteId === id) {
        appMessageApi.error('Failed to process search console account');
      }
    }
  });
};
